import * as THREE from 'three'
import { GSplatScanLUTWriter } from '@/plugins/gsplat/effects/boxScanLut'
import {
  createColor,
  createVector3,
  type GSplatColorInput,
  type GSplatVector3Input,
  writeVector3Array,
} from '@/plugins/gsplat/effects/effectUtils'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'

const shaderGLSL = /* glsl */ `
uniform sampler2D uLUT;
uniform vec3 uAabbMin;
uniform vec3 uAabbMax;
uniform vec3 uSweepMin;
uniform vec3 uSweepMax;
uniform vec3 uDirection;

bool g_insideAABB;
vec4 g_lutValue;

void modifySplatCenter(inout vec3 center) {
  g_insideAABB = all(greaterThanEqual(center, uAabbMin)) && all(lessThanEqual(center, uAabbMax));
  if (!g_insideAABB) {
    return;
  }

  float dirLen = length(uDirection);
  if (dirLen < 0.001) {
    g_insideAABB = false;
    return;
  }

  vec3 absDir = abs(uDirection / dirLen);
  vec3 relPos = center - uSweepMin;
  vec3 sweepSize = uSweepMax - uSweepMin;
  float boxLength = dot(sweepSize, absDir);
  float splatPos = dot(relPos, absDir);
  float t = clamp(splatPos / max(boxLength, 0.0001), 0.0, 1.0);
  int texelX = int(t * 255.0);
  g_lutValue = texelFetch(uLUT, ivec2(texelX, 0), 0);
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  if (!g_insideAABB) return;

  float lutScale = g_lutValue.a;
  if (lutScale < 0.01) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  }

  if (lutScale < 1.0) {
    gsplatApplyUniformScale(covA, covB, lutScale);
  }
}

void modifySplatColor(vec3 center, inout vec4 color) {
  if (!g_insideAABB) return;
  color.rgb *= g_lutValue.rgb;
}
`

const shaderWGSL = /* wgsl */ `
var<private> g_insideAABB: bool;
var<private> g_lutValue: vec4f;

fn modifySplatCenter(center: ptr<function, vec3f>) {
  g_insideAABB = all((*center) >= uniform.uAabbMin) &&
    all((*center) <= uniform.uAabbMax);
  if (!g_insideAABB) { return; }

  let directionLength = length(uniform.uDirection);
  if (directionLength < 0.001) {
    g_insideAABB = false;
    return;
  }

  let absoluteDirection = abs(uniform.uDirection / directionLength);
  let relativePosition = (*center) - uniform.uSweepMin;
  let sweepSize = uniform.uSweepMax - uniform.uSweepMin;
  let boxLength = dot(sweepSize, absoluteDirection);
  let splatPosition = dot(relativePosition, absoluteDirection);
  let t = clamp(splatPosition / max(boxLength, 0.0001), 0.0, 1.0);
  let texelX = i32(t * 255.0);
  g_lutValue = textureLoad(uLUT, vec2i(texelX, 0), 0);
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  if (!g_insideAABB) { return; }

  let lutScale = g_lutValue.a;
  if (lutScale < 0.01) {
    *scale = vec3f(0.0);
    return;
  }
  if (lutScale < 1.0) {
    *scale = (*scale) * lutScale;
  }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  if (!g_insideAABB) { return; }
  (*color) = vec4f((*color).rgb * g_lutValue.rgb, (*color).a);
}
`

export interface GSplatBoxShaderEffectOptions extends GSplatShaderEffectOptions {
  aabbMin?: GSplatVector3Input
  aabbMax?: GSplatVector3Input
  sweepMin?: GSplatVector3Input
  sweepMax?: GSplatVector3Input
  direction?: GSplatVector3Input
  duration?: number
  visibleStart?: boolean
  visibleEnd?: boolean
  interval?: number
  invertTint?: boolean
  baseTint?: GSplatColorInput
  edgeTint?: GSplatColorInput
  tint?: GSplatColorInput
}

export class GSplatBoxShaderEffect extends GSplatShaderEffect {
  aabbMin: THREE.Vector3
  aabbMax: THREE.Vector3
  sweepMin: THREE.Vector3 | null
  sweepMax: THREE.Vector3 | null
  direction: THREE.Vector3
  duration: number
  visibleStart: boolean
  visibleEnd: boolean
  interval: number
  invertTint: boolean
  baseTint: THREE.Color
  edgeTint: THREE.Color
  tint: THREE.Color

  private readonly _aabbMinArray = [0, 0, 0]
  private readonly _aabbMaxArray = [0, 0, 0]
  private readonly _sweepMinArray = [0, 0, 0]
  private readonly _sweepMaxArray = [0, 0, 0]
  private readonly _directionArray = [0, 0, 0]
  private readonly _lutData = new Uint16Array(256 * 4)
  private readonly _normDir = new THREE.Vector3()
  private readonly _absDir = new THREE.Vector3()
  private readonly _boxSize = new THREE.Vector3()
  private readonly _lutWriter = new GSplatScanLUTWriter()
  private _lutTexture: THREE.DataTexture | null = null

  constructor(options: GSplatBoxShaderEffectOptions = {}) {
    super(options)
    this.aabbMin = createVector3(options.aabbMin, new THREE.Vector3(-0.5, -0.5, -0.5))
    this.aabbMax = createVector3(options.aabbMax, new THREE.Vector3(0.5, 0.5, 0.5))
    this.sweepMin = options.sweepMin ? createVector3(options.sweepMin, this.aabbMin) : null
    this.sweepMax = options.sweepMax ? createVector3(options.sweepMax, this.aabbMax) : null
    this.direction = createVector3(options.direction, new THREE.Vector3(0, 1, 0))
    this.duration = options.duration ?? 1
    this.visibleStart = options.visibleStart ?? false
    this.visibleEnd = options.visibleEnd ?? true
    this.interval = options.interval ?? 0.2
    this.invertTint = options.invertTint ?? false
    this.baseTint = createColor(options.baseTint, new THREE.Color(1, 1, 1))
    this.edgeTint = createColor(options.edgeTint, new THREE.Color(1, 0, 1))
    this.tint = createColor(options.tint, new THREE.Color(1, 1, 1))
    this._lutTexture = this.createLUTTexture()
  }

  getShaderGLSL(): string {
    return shaderGLSL
  }

  getShaderWGSL(): string {
    return shaderWGSL
  }

  get lutTexture(): THREE.DataTexture | null {
    return this._lutTexture
  }

  generateLUT(): void {
    const dirLen = this.direction.length()
    if (dirLen < 0.001) {
      this._lutWriter.fill(this._lutData, 1, 1, 1, 1)
      if (this._lutTexture) {
        this._lutTexture.needsUpdate = true
      }
      return
    }

    this._normDir.copy(this.direction).normalize()
    this._absDir.set(
      Math.abs(this._normDir.x),
      Math.abs(this._normDir.y),
      Math.abs(this._normDir.z)
    )

    this._boxSize.subVectors(this.sweepMax ?? this.aabbMax, this.sweepMin ?? this.aabbMin)
    const boxLength = this._boxSize.dot(this._absDir)
    const isNegativeDir = this._normDir.x + this._normDir.y + this._normDir.z < 0
    this._lutWriter.write(this._lutData, {
      length: boxLength,
      effectTime: this.effectTime,
      duration: this.duration,
      interval: this.interval,
      reverse: isNegativeDir,
      visibleStart: this.visibleStart,
      visibleEnd: this.visibleEnd,
      invertTint: this.invertTint,
      baseTint: this.baseTint,
      edgeTint: this.edgeTint,
      tint: this.tint,
    })

    if (this._lutTexture) {
      this._lutTexture.needsUpdate = true
    }
  }

  protected override updateEffect(): void {
    this.generateLUT()
    this.setUniform('uLUT', this._lutTexture)
    this.setUniform('uAabbMin', writeVector3Array(this.aabbMin, this._aabbMinArray))
    this.setUniform('uAabbMax', writeVector3Array(this.aabbMax, this._aabbMaxArray))
    this.setUniform(
      'uSweepMin',
      writeVector3Array(this.sweepMin ?? this.aabbMin, this._sweepMinArray)
    )
    this.setUniform(
      'uSweepMax',
      writeVector3Array(this.sweepMax ?? this.aabbMax, this._sweepMaxArray)
    )
    this.setUniform('uDirection', writeVector3Array(this.direction, this._directionArray))
  }

  protected override onDestroy(): void {
    this._lutTexture?.dispose()
    this._lutTexture = null
  }

  private createLUTTexture(): THREE.DataTexture {
    const texture = new THREE.DataTexture(
      this._lutData,
      256,
      1,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    texture.name = 'GSplatBoxShaderEffectLUT'
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    texture.generateMipmaps = false
    texture.unpackAlignment = 1
    texture.needsUpdate = true
    return texture
  }
}
